import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { getGitHubInstallUrl, getGitHubAuthUrl } from "../api";
import { useAuth } from "../auth/AuthContext";
import GitHubPermissionsNote from "./GitHubPermissionsNote";

const JOIN_ERROR_MESSAGES = {
  invite_code_not_found: "This invite code is no longer valid — check for typos or ask for a fresh link.",
  invite_access_denied: (teamName, username) =>
    `You're signed in as @${username}, but don't have access to ${teamName ? `${teamName}'s` : "that team's"} GitHub organization.`,
  invite_verification_failed: "Something went wrong checking your GitHub access — try again.",
  // Item 9: hard member cap, raised only when the target team is on Free
  // and already has 5 active members.
  team_at_member_limit: (teamName) =>
    `${teamName ? `${teamName} is` : "That team is"} at its Free plan's member limit. Ask a team admin to upgrade to Pro to add more teammates.`,
};

/**
 * The guided setup wizard, at /onboarding.
 *
 * It used to render *inside* /dashboard, chosen by a localStorage preference
 * (`teamlessView`). That gave it no address — you could not link to it,
 * bookmark it, or navigate back to it — and made "which screen does /dashboard
 * show?" depend on browser storage with four ways to desync. See gotcha #54.
 *
 * As a route it reads its own inputs from context rather than taking props,
 * and the teamless panel links here instead of a flag being flipped.
 */
function Onboarding() {
  const { user, hasTeam, joinError, pendingJoinCode } = useAuth();
  const inviteCode = pendingJoinCode;
  const [mode, setMode] = useState(inviteCode || joinError ? "join" : "install");
  const [joinCode, setJoinCode] = useState(inviteCode || "");

  // `=== true`, not truthiness: hasTeam is null while the team is still
  // resolving, and redirecting on null would bounce someone off the wizard
  // before we know whether they need it. Same distinction Dashboard documents
  // for `=== false`.
  if (hasTeam === true) {
    return <Navigate to="/dashboard" replace />;
  }

  // Installing navigates away to GitHub, which handles org and repo selection
  // and then returns through auth-callback with an installation_id — that's
  // where the team gets created. Nothing to submit here.
  const handleInstall = () => {
    window.location.href = getGitHubInstallUrl();
  };

  // Joining now round-trips through GitHub too (item 15) — the code rides in
  // `state` so auth-callback can verify real org access before joining,
  // rather than the old DB-only POST /teams/join. The join itself, and any
  // failure, happens server-side; this button's only job is to kick off that
  // round-trip.
  const handleJoinTeam = (e) => {
    e.preventDefault();
    if (!joinCode.trim()) return;
    window.location.href = getGitHubAuthUrl(joinCode.trim());
  };

  const joinErrorMessage = joinError
    ? typeof JOIN_ERROR_MESSAGES[joinError.code] === "function"
      ? JOIN_ERROR_MESSAGES[joinError.code](joinError.teamName, user?.github_username)
      : JOIN_ERROR_MESSAGES[joinError.code] || "Couldn't join that team — try again."
    : null;

  return (
    <div className="min-h-screen bg-rf-bg-primary flex items-center justify-center">
      <div className="bg-rf-bg-secondary rounded-2xl p-10 max-w-lg w-full mx-4 border border-rf-border">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-rf-accent to-rf-accent-blue flex items-center justify-center text-white font-bold text-lg">
            A
          </div>
          <span className="text-2xl font-bold text-rf-text-primary tracking-tight">
            Anthurium
          </span>
        </div>

        {/* Item 20: safety net for whoever lands here cold — most commonly
            someone who clicked "Sign in" (or hit /login some other way)
            expecting an existing account, and turned out to have no team.
            Genuine new users going through /signup never reach this screen
            at all (that flow always resolves a team immediately), so this
            is a secondary defense now, not the primary catch. Scoped to
            !inviteCode && !joinError specifically — anyone arriving with a
            real join intent or a join failure already has clearer context
            from the tab/error copy below, and doesn't need this too. */}
        {!inviteCode && !joinError && (
          <div className="mb-6 p-4 rounded-lg bg-rf-accent/10 border border-rf-accent/30 text-rf-text-primary text-sm text-center">
            👋 Welcome — looks like you don't have a Anthurium account yet. Let's get your team set up.
          </div>
        )}

        <div className="flex gap-2 mb-6">
          <button
            type="button"
            onClick={() => setMode("install")}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
              mode === "install"
                ? "bg-rf-accent text-white"
                : "bg-rf-bg-tertiary text-rf-text-secondary hover:text-rf-text-primary"
            }`}
          >
            Set up a team
          </button>
          <button
            type="button"
            onClick={() => setMode("join")}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
              mode === "join"
                ? "bg-rf-accent text-white"
                : "bg-rf-bg-tertiary text-rf-text-secondary hover:text-rf-text-primary"
            }`}
          >
            Join with invite code
          </button>
        </div>

        {mode === "install" ? (
          <>
            <h2 className="text-xl font-semibold text-rf-text-primary text-center mb-2">
              Install Anthurium on GitHub
            </h2>
            <p className="text-sm text-rf-text-secondary text-center mb-6">
              You'll pick which organization and repositories Anthurium can
              see. Your team is created from that installation.
            </p>

            {/* Explicit about what we do to their repos before they authorize —
                this touches company infrastructure, so it shouldn't be a surprise. */}
            {/* Extracted to a shared component when TeamlessPanel became a
                second surface that can start the same authorization — two
                copies of a claim like this drift invisibly. */}
            <div className="mb-6">
              <GitHubPermissionsNote />
            </div>

            <button
              onClick={handleInstall}
              className="w-full py-3 rounded-lg bg-rf-accent text-white font-semibold text-sm hover:bg-rf-accent/90 transition-colors"
            >
              Continue to GitHub
            </button>
          </>
        ) : (
          <>
            <h2 className="text-xl font-semibold text-rf-text-primary text-center mb-2">
              Join your team
            </h2>
            <p className="text-sm text-rf-text-secondary text-center mb-6">
              Enter the invite code your teammate shared with you. No GitHub
              installation needed — whoever set up the team already did that.
            </p>

            {joinErrorMessage && (
              <div className="mb-4 p-3 rounded-lg bg-rf-accent-danger/10 border border-rf-accent-danger/30 text-rf-accent-danger text-sm">
                {joinErrorMessage}
              </div>
            )}

            <form onSubmit={handleJoinTeam} className="space-y-4">
              <div>
                <label className="block text-xs text-rf-text-secondary mb-1.5 uppercase tracking-wider">
                  Invite Code
                </label>
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value)}
                  placeholder="Paste your invite code"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-rf-border bg-rf-bg-tertiary text-rf-text-primary text-sm font-mono outline-none focus:border-rf-accent-blue/50"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full py-3 rounded-lg bg-rf-accent text-white font-semibold text-sm hover:bg-rf-accent/90 transition-colors"
              >
                {joinErrorMessage ? "Try again" : "Join Team"}
              </button>
            </form>
          </>
        )}

        {/* A real link now, not a flag flip. It used to call showTeamlessHome()
            to switch a stored mode; the dashboard is simply a place you can go,
            so leaving is navigation.

            Still scoped to someone with no join intent — an invited teammate
            has a specific thing to do here, and a failed join needs retrying
            rather than dismissing.

            No trial consequence to reason about: the 14-day Pro trial is
            granted by auth-callback's INSERT INTO teams and is scoped to the
            GitHub account rather than the team, so leaving neither burns nor
            starts anything. The org's one trial is simply still unclaimed. */}
        {!inviteCode && !joinError && (
          <Link
            to="/dashboard"
            className="block w-full mt-4 py-2 text-center text-xs text-rf-text-secondary hover:text-rf-text-primary transition-colors"
          >
            Skip for now — explore the dashboard
          </Link>
        )}
      </div>
    </div>
  );
}

export default Onboarding;
